import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '@/lib/api';
import { errorMessage } from '@/lib/copy';
import { Button } from '@/components/ui/button';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader } from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toast';
import { useRequestChanges } from './queries';

/* ---------------------------------------------------------------------------
   REQUEST CHANGES — the expert's exit that is not a promotion.

   It is the one moment of the whole review the client is meant to perceive, and
   the note is the ONLY message an expert writes that a client reads. Everything
   else in the thread stays hidden. So this dialog is deliberately the only place
   in the app where the composer's hatched band is absent and replaced by the
   opposite warning: this one IS visible.

   What pressing it does, all at once: moves the chat to `changes requested`,
   UNFREEZES the project, releases the claim, and posts the note. The client's
   composer unlocks and their next prompt continues on the same already-modified
   `pre-staging` branch. It is not reversible from this side — reclaiming the
   chat requires the client to come back — which is why the consequence is spelled
   out rather than summarised as "are you sure".

   THE NOTE IS SCRUBBED ON WRITE like any client-facing string: it must not name
   the agent vendor, a repo, a SHA, or the expert. That is enforced server-side,
   but saying it here is what stops a reviewer writing three paragraphs of repo
   detail and wondering why the client received something shorter.
--------------------------------------------------------------------------- */

const MIN_LENGTH = 12;

export function RequestChangesDialog({
  chatId,
  open,
  onOpenChange,
}: {
  chatId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [message, setMessage] = React.useState('');
  const navigate = useNavigate();
  const requestChanges = useRequestChanges(chatId);

  React.useEffect(() => {
    if (open) setMessage('');
  }, [open]);

  const trimmed = message.trim();
  const tooShort = trimmed.length > 0 && trimmed.length < MIN_LENGTH;
  const canSend = trimmed.length >= MIN_LENGTH && !requestChanges.isPending;

  const submit = () => {
    if (!canSend) return;
    requestChanges.mutate(
      { message: trimmed },
      {
        onSuccess: () => {
          toast(
            'ok',
            'Handed back to the client',
            'The chat is in changes requested, the project is unfrozen and your claim is released.',
          );
          onOpenChange(false);
          navigate('/queue');
        },
        onError: (error) => {
          toast(
            'error',
            'That could not be sent',
            error instanceof ApiError ? errorMessage(error.code, error.message) : 'The request failed.',
          );
        },
      },
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (requestChanges.isPending && !next) return;
        onOpenChange(next);
      }}
    >
      <DialogContent width="md" onEscapeKeyDown={(event) => requestChanges.isPending && event.preventDefault()}>
        <DialogHeader
          title="Request changes"
          description="This hands the thread back to the client, unfreezes the project and releases your claim. It is the only message you write that they read."
        />

        <DialogBody>
          <div className="border border-action bg-action-soft px-2.5 py-2">
            <p className="eyebrow text-action">The client reads this</p>
            <p className="mt-1 text-xs leading-[17px] text-ink-2">
              Write it as feedback on what they asked for. It is scrubbed on write, so it cannot carry a repo name, a
              SHA, an agent vendor or your name — and the client has no idea a review happened beyond this note.
            </p>
          </div>

          <Field
            label="Note to the client"
            htmlFor="request-changes-note"
            required
            error={tooShort ? 'A note this short will not tell the client what to change.' : undefined}
          >
            <Textarea
              id="request-changes-note"
              rows={5}
              value={message}
              invalid={tooShort}
              onChange={(event) => setMessage(event.target.value)}
              placeholder="The VAT total is right, but it should also appear on the printed invoice."
            />
          </Field>

          <p className="text-xs leading-[17px] text-ink-3">
            Done is not offered from <span className="font-mono">changes requested</span>: the client answers this note
            by prompting, and the chat comes back to review only after that run completes. Their next prompt continues on
            the same already-modified branch, so nothing you reviewed is lost.
          </p>
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" disabled={requestChanges.isPending} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!canSend} loading={requestChanges.isPending} onClick={submit}>
            Request changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
